'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Volume2, Settings, X, Loader2, Square } from 'lucide-react';
import type { VoiceModel } from '@/components/tts/ttsTypes';
import { SPEAKERS, LANGUAGES } from '@/components/tts/ttsConstants';
import {
  synthesizeQwen3,
  synthesizeBertVits2,
  synthesizeIrodori,
  fetchIrodoriVoices,
} from '@/components/tts/ttsApi';
import { useAppStore } from '@/store/useAppStore';
import CharacterPicker from '@/components/voice-preset/CharacterPicker';
import { useCharacterVoicePreset } from '@/hooks/useCharacterVoicePreset';
import type { CharacterKey } from '@/lib/voicePreset/types';
import type { TTSSelection } from '@/lib/tts';

export type AiTextTTSProvider = 'qwen3' | 'bert-vits2' | 'irodori';

const LS_CHARACTER_KEY = 'ai-text-tts-character-key';
const LS_PROVIDER = 'ai-text-tts-provider';
const LS_QWEN3_SPEAKER = 'ai-text-tts-qwen3-speaker';
const LS_QWEN3_LANGUAGE = 'ai-text-tts-qwen3-language';
const LS_BV2_MODEL = 'ai-text-tts-bv2-model';
const LS_BV2_SPEAKER = 'ai-text-tts-bv2-speaker';
const LS_BV2_STYLE = 'ai-text-tts-bv2-style';
const LS_IRODORI_VOICE = 'ai-text-tts-irodori-voice';
const LS_IRODORI_SPEED = 'ai-text-tts-irodori-speed';

function readLs(key: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}
function writeLs(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}

function stripMarkdown(s: string): string {
  return s
    .replace(/\*\*([^*]+)\*\*/g, '$1')        // **bold**
    .replace(/\*([^*]+)\*/g, '$1')            // *italic*
    .replace(/(?<!\w)__([^_]+)__(?!\w)/g, '$1')   // __bold__ (word boundary)
    .replace(/(?<!\w)_([^_]+)_(?!\w)/g, '$1')     // _italic_ (word boundary)
    .replace(/`([^`]+)`/g, '$1')              // `code`
    .replace(/^#+\s+/gm, '')                  // # headings
    .replace(/^>\s*/gm, '')                   // > quotes
    .replace(/^[-*]\s+/gm, '')                // - bullets
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // [text](url)
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '');    // ![alt](img)
}

function showToast(message: string, type: 'success' | 'error' = 'success') {
  if (typeof window === 'undefined') return;
  const div = document.createElement('div');
  div.textContent = message;
  div.style.cssText = `
    position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
    padding: 8px 16px; border-radius: 6px; color: white; z-index: 9999;
    background: ${type === 'success' ? '#16a34a' : '#dc2626'};
    font-size: 13px; pointer-events: none;
  `;
  document.body.appendChild(div);
  setTimeout(() => div.remove(), 2500);
}

export interface AiTextTTSProps {
  text: string;
}

export default function AiTextTTS({ text }: AiTextTTSProps) {
  const qwen3Status = useAppStore((s) => s.qwenTtsServerStatus);

  // --- Character key (voice preset) ---
  const [characterKey, setCharacterKey] = useState<CharacterKey | null>(() => {
    try {
      const raw = localStorage.getItem(LS_CHARACTER_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as CharacterKey;
    } catch { return null; }
  });

  useEffect(() => {
    if (characterKey) {
      localStorage.setItem(LS_CHARACTER_KEY, JSON.stringify(characterKey));
    } else {
      localStorage.removeItem(LS_CHARACTER_KEY);
    }
  }, [characterKey]);

  const { preset: characterPreset } = useCharacterVoicePreset(characterKey);

  // --- Provider ---
  const [provider, setProvider] = useState<AiTextTTSProvider>(() => {
    const v = readLs(LS_PROVIDER, 'qwen3');
    return (v === 'qwen3' || v === 'bert-vits2' || v === 'irodori') ? v : 'qwen3';
  });

  // --- Qwen3 ---
  const [qwen3Speaker, setQwen3Speaker] = useState(() => readLs(LS_QWEN3_SPEAKER, 'ono_anna'));
  const [qwen3Language, setQwen3Language] = useState(() => readLs(LS_QWEN3_LANGUAGE, 'Japanese'));

  // --- Bert-VITS2 ---
  const [bv2ModelId, setBv2ModelId] = useState(() => readLs(LS_BV2_MODEL, ''));
  const [bv2Speaker, setBv2Speaker] = useState(() => readLs(LS_BV2_SPEAKER, ''));
  const [bv2Style, setBv2Style] = useState(() => readLs(LS_BV2_STYLE, 'Neutral'));

  // --- Irodori ---
  const [irodoriVoice, setIrodoriVoice] = useState(() => readLs(LS_IRODORI_VOICE, 'none'));
  const [irodoriSpeed, setIrodoriSpeed] = useState<number>(() => {
    const raw = readLs(LS_IRODORI_SPEED, '1');
    const n = parseFloat(raw);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });

  // --- Persistence ---
  useEffect(() => { writeLs(LS_PROVIDER, provider); }, [provider]);
  useEffect(() => { writeLs(LS_QWEN3_SPEAKER, qwen3Speaker); }, [qwen3Speaker]);
  useEffect(() => { writeLs(LS_QWEN3_LANGUAGE, qwen3Language); }, [qwen3Language]);
  useEffect(() => { writeLs(LS_BV2_MODEL, bv2ModelId); }, [bv2ModelId]);
  useEffect(() => { writeLs(LS_BV2_SPEAKER, bv2Speaker); }, [bv2Speaker]);
  useEffect(() => { writeLs(LS_BV2_STYLE, bv2Style); }, [bv2Style]);
  useEffect(() => { writeLs(LS_IRODORI_VOICE, irodoriVoice); }, [irodoriVoice]);
  useEffect(() => { writeLs(LS_IRODORI_SPEED, String(irodoriSpeed)); }, [irodoriSpeed]);

  // --- Server status (Task 3 で実装) ---
  const [bv2Status, setBv2Status] = useState<'online' | 'offline' | 'checking'>('checking');
  const [irodoriStatus, setIrodoriStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const [bv2Models, setBv2Models] = useState<VoiceModel[]>([]);
  const [irodoriVoices, setIrodoriVoices] = useState<string[]>(['none']);

  // --- Playback (Task 5 で実装) ---
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  // --- Fetch Bert-VITS2 / Irodori status ---
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await fetch('/api/voice/status');
        if (!res.ok) throw new Error('Status fetch failed');
        const data = await res.json();
        if (cancelled) return;
        if (data.providers?.['bert-vits2']) {
          setBv2Status(data.providers['bert-vits2'].online ? 'online' : 'offline');
          setBv2Models((data.providers['bert-vits2'].models || []) as VoiceModel[]);
        } else {
          setBv2Status('offline');
        }
        if (data.providers?.['irodori']) {
          setIrodoriStatus(data.providers['irodori'].online ? 'online' : 'offline');
        } else {
          setIrodoriStatus('offline');
        }
      } catch {
        if (cancelled) return;
        setBv2Status('offline');
        setIrodoriStatus('offline');
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Fetch Irodori voice list when online
  useEffect(() => {
    if (irodoriStatus !== 'online') return;
    let cancelled = false;
    fetchIrodoriVoices().then((voices) => {
      if (!cancelled) setIrodoriVoices(voices);
    });
    return () => { cancelled = true; };
  }, [irodoriStatus]);

  // NOTE: bv2CurrentModel must stay declared BEFORE `manualSelection` below —
  // manualSelection reads bv2CurrentModel?.name for the bert-vits2 branch.
  const bv2CurrentModel = bv2Models.find((m) => m.id === bv2ModelId);

  // --- Effective selection: preset overrides manual controls ---
  const manualSelection: TTSSelection =
    provider === 'qwen3' ? {
      provider: 'qwen3',
      mode: 'synthesize',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      speaker: qwen3Speaker as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      language: qwen3Language as any,
      outputFormat: 'wav',
    } :
    provider === 'bert-vits2' ? {
      provider: 'bert-vits2',
      modelId: bv2ModelId,
      modelName: bv2CurrentModel?.name ?? '',
      speakerName: bv2Speaker,
      style: bv2Style || 'Neutral',
    } :
    {
      provider: 'irodori',
      voice: irodoriVoice || 'none',
      speed: irodoriSpeed,
    };

  const effectiveSelection: TTSSelection = characterPreset?.selection ?? manualSelection;

  const effectiveProvider = effectiveSelection.provider;

  const currentStatus =
    effectiveProvider === 'qwen3'      ? qwen3Status :
    effectiveProvider === 'bert-vits2' ? bv2Status   :
                                          irodoriStatus;

  const statusDot = (s: 'online' | 'offline' | 'checking') => (
    <span
      className={`inline-block w-2 h-2 rounded-full ml-1 ${
        s === 'online' ? 'bg-green-500' : s === 'offline' ? 'bg-red-400' : 'bg-yellow-400'
      }`}
      title={s}
    />
  );

  // Auto-select first available model/speaker for Bert-VITS2 when models load
  useEffect(() => {
    if (bv2Models.length === 0) return;
    const current = bv2Models.find((m) => m.id === bv2ModelId) ?? bv2Models[0];
    if (current.id !== bv2ModelId) setBv2ModelId(current.id);
    if (!current.speakers.includes(bv2Speaker) && current.speakers[0]) {
      setBv2Speaker(current.speakers[0]);
    }
    if (!current.styles.includes(bv2Style) && current.styles[0]) {
      setBv2Style(current.styles[0]);
    }
  }, [bv2Models, bv2ModelId, bv2Speaker, bv2Style]);

  const isHandlingRef = useRef(false);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setIsPlaying(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => { stopAudio(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlayClick = async () => {
    if (isHandlingRef.current) return;
    if (isPlaying) {
      stopAudio();
      return;
    }
    isHandlingRef.current = true;
    if (!text.trim()) {
      showToast('テキストがありません', 'error');
      return;
    }
    if (currentStatus !== 'online') {
      showToast('サーバーが起動していません', 'error');
      return;
    }

    setIsGenerating(true);
    try {
      const cleaned = stripMarkdown(text);
      let blob: Blob;
      if (effectiveSelection.provider === 'qwen3') {
        blob = await synthesizeQwen3({
          text: cleaned,
          language: effectiveSelection.language,
          speaker: effectiveSelection.speaker,
          mode: effectiveSelection.mode ?? 'synthesize',
          outputFormat: effectiveSelection.outputFormat ?? 'wav',
          serverUrl: (typeof window !== 'undefined' && localStorage.getItem('qwenTtsServerUrl')) || 'http://localhost:8771',
        });
      } else if (effectiveSelection.provider === 'bert-vits2') {
        if (!effectiveSelection.modelId || !effectiveSelection.speakerName) {
          showToast('Bert-VITS2 のモデル/話者が選択されていません', 'error');
          setIsGenerating(false);
          return;
        }
        blob = await synthesizeBertVits2({
          text: cleaned,
          modelId: effectiveSelection.modelId,
          speakerName: effectiveSelection.speakerName,
          style: effectiveSelection.style || 'Neutral',
        });
      } else {
        blob = await synthesizeIrodori({
          text: cleaned,
          voice: effectiveSelection.voice || 'none',
          speed: effectiveSelection.speed,
        });
      }

      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (audioUrlRef.current === url) audioUrlRef.current = null;
        audioRef.current = null;
        setIsPlaying(false);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        if (audioUrlRef.current === url) audioUrlRef.current = null;
        audioRef.current = null;
        setIsPlaying(false);
        showToast('再生に失敗しました', 'error');
      };
      audioRef.current = audio;
      audioUrlRef.current = url;
      await audio.play();
      setIsPlaying(true);
    } catch (err) {
      stopAudio();
      const msg = err instanceof Error ? err.message : '生成に失敗しました';
      showToast(msg, 'error');
    } finally {
      isHandlingRef.current = false;
      setIsGenerating(false);
    }
  };

  // --- UI ---
  const [showSettings, setShowSettings] = useState(false);
  const gearButtonRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; right: number } | null>(null);

  // popup の位置を gear ボタンの位置から計算 (viewport 基準, position: fixed 用)
  useLayoutEffect(() => {
    if (!showSettings) return;
    const updatePos = () => {
      const btn = gearButtonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setPopupPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [showSettings]);

  // popup 外クリックで閉じる
  useEffect(() => {
    if (!showSettings) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popupRef.current?.contains(target)) return;
      if (gearButtonRef.current?.contains(target)) return;
      setShowSettings(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showSettings]);

  return (
    <div className="relative inline-flex items-center gap-1">
      <button
        type="button"
        onClick={handlePlayClick}
        disabled={isGenerating || (currentStatus !== 'online' && !isPlaying)}
        className={`flex items-center gap-1 px-2 py-0.5 text-xs border rounded disabled:opacity-40 disabled:cursor-not-allowed ${
          isPlaying
            ? 'border-red-300 text-red-600 hover:bg-red-50'
            : 'border-gray-200 hover:bg-gray-50'
        }`}
        title={
          currentStatus !== 'online'
            ? `${effectiveProvider} サーバーが起動していません`
            : isGenerating ? '生成中...'
            : isPlaying ? '停止'
            : '読み上げ'
        }
      >
        {isGenerating ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : isPlaying ? (
          <Square className="w-3 h-3" />
        ) : (
          <Volume2 className="w-3 h-3" />
        )}
        {isGenerating ? '生成中' : isPlaying ? '停止' : '再生'}
      </button>

      {/* Settings toggle */}
      <button
        ref={gearButtonRef}
        type="button"
        onClick={() => setShowSettings((v) => !v)}
        className="flex items-center px-1.5 py-0.5 text-xs border border-gray-200 rounded hover:bg-gray-50"
        title="TTS 設定"
      >
        <Settings className="w-3 h-3" />
      </button>

      {showSettings && popupPos && typeof document !== 'undefined' && createPortal(
        <div
          ref={popupRef}
          style={{ position: 'fixed', top: popupPos.top, right: popupPos.right }}
          className="z-[1000] w-72 p-3 bg-white border border-gray-200 rounded shadow-lg text-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-gray-700">TTS 設定</span>
            <button
              type="button"
              onClick={() => setShowSettings(false)}
              className="text-gray-400 hover:text-gray-700"
            >
              <X className="w-3 h-3" />
            </button>
          </div>

          {/* Character picker */}
          <div className="mb-3 border-b pb-3">
            <label className="block text-xs text-gray-600 mb-1">話者キャラ</label>
            <CharacterPicker
              value={characterKey}
              onChange={setCharacterKey}
              placeholder="(未選択 - 手動)"
            />
          </div>

          {/* Manual controls (disabled when preset active) */}
          <div className={characterPreset ? 'opacity-50 pointer-events-none' : ''}>

          {/* Provider selector */}
          <div className="mb-3">
            <label className="block text-[10px] text-gray-500 mb-1">プロバイダー</label>
            <div className="grid grid-cols-2 gap-1">
              {([
                { v: 'qwen3' as const,       label: 'Qwen3',      status: qwen3Status   },
                { v: 'bert-vits2' as const,  label: 'Bert-VITS2', status: bv2Status     },
                { v: 'irodori' as const,     label: 'Irodori',    status: irodoriStatus },
              ]).map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setProvider(opt.v)}
                  className={`flex items-center justify-center gap-0.5 px-1 py-1 border rounded ${
                    provider === opt.v
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}{statusDot(opt.status)}
                </button>
              ))}
            </div>
          </div>

          {/* Qwen3 settings */}
          {provider === 'qwen3' && (
            <div className="space-y-2">
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">話者</label>
                <select
                  value={qwen3Speaker}
                  onChange={(e) => setQwen3Speaker(e.target.value)}
                  className="w-full px-1.5 py-1 border border-gray-200 rounded"
                  disabled={qwen3Status === 'offline'}
                >
                  {SPEAKERS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.gender === 'female' ? '女性' : '男性'}, {s.lang})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">言語</label>
                <select
                  value={qwen3Language}
                  onChange={(e) => setQwen3Language(e.target.value)}
                  className="w-full px-1.5 py-1 border border-gray-200 rounded"
                  disabled={qwen3Status === 'offline'}
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Bert-VITS2 settings */}
          {provider === 'bert-vits2' && (
            <div className="space-y-2">
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">モデル</label>
                <select
                  value={bv2ModelId}
                  onChange={(e) => setBv2ModelId(e.target.value)}
                  className="w-full px-1.5 py-1 border border-gray-200 rounded"
                  disabled={bv2Models.length === 0}
                >
                  {bv2Models.length === 0
                    ? <option value="">(サーバー未起動)</option>
                    : bv2Models.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">話者</label>
                <select
                  value={bv2Speaker}
                  onChange={(e) => setBv2Speaker(e.target.value)}
                  className="w-full px-1.5 py-1 border border-gray-200 rounded"
                  disabled={!bv2CurrentModel || bv2CurrentModel.speakers.length === 0}
                >
                  {(bv2CurrentModel?.speakers ?? []).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">スタイル</label>
                <select
                  value={bv2Style}
                  onChange={(e) => setBv2Style(e.target.value)}
                  className="w-full px-1.5 py-1 border border-gray-200 rounded"
                  disabled={!bv2CurrentModel || bv2CurrentModel.styles.length === 0}
                >
                  {(bv2CurrentModel?.styles ?? []).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Irodori settings */}
          {provider === 'irodori' && (
            <div className="space-y-2">
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">音声</label>
                <select
                  value={irodoriVoice}
                  onChange={(e) => setIrodoriVoice(e.target.value)}
                  className="w-full px-1.5 py-1 border border-gray-200 rounded"
                  disabled={irodoriStatus !== 'online'}
                >
                  {irodoriVoices.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">
                  Speed: {irodoriSpeed.toFixed(2)}
                </label>
                <input
                  type="range"
                  min={0.5}
                  max={2.0}
                  step={0.05}
                  value={irodoriSpeed}
                  onChange={(e) => setIrodoriSpeed(parseFloat(e.target.value))}
                  className="w-full"
                  disabled={irodoriStatus !== 'online'}
                />
              </div>
            </div>
          )}

          </div>{/* end manual controls */}

          <div className="mt-3 pt-2 border-t border-gray-100 text-[10px] text-gray-500">
            現在: <span className="font-mono">{effectiveProvider}</span> ({currentStatus})
            {characterPreset && (
              <span className="ml-1 text-indigo-500">[プリセット: {characterPreset.name}]</span>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
